import { styled } from 'styled-components';
import { Icon, useThemeColor } from '@wistia/vhs';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import type { Nilable } from '~/utilities/type-guards';
import { isNil, isNonEmptyString } from '~/utilities/type-guards';
import { generateContactAvatarColors } from './utils/generateContactAvatarColors';

const AvatarImage = styled.img<{
  heightAndWidth: number;
  avatarBorderColor?: Nilable<string>;
  showBorder: boolean;
}>`
  align-items: center;
  background-color: white;
  border-radius: 50%;
  color: white;
  display: flex;
  height: ${({ heightAndWidth }) => heightAndWidth}px;
  justify-content: center;
  object-fit: cover;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  width: ${({ heightAndWidth }) => heightAndWidth}px;

  ${({ showBorder, avatarBorderColor, theme }) =>
    showBorder && { border: `2px solid ${avatarBorderColor ?? theme.color.grey200}` }}

  a:hover & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 25%);
  }

  a:active & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 50%);
  }
`;

const AvatarIcon = styled.div<{
  heightAndWidth: number;
  avatarBackgroundColor?: Nilable<string>;
  avatarIconColor?: Nilable<string>;
  avatarBorderColor?: Nilable<string>;
  showBorder: boolean;
}>`
  align-items: center;
  background-color: ${({ theme, avatarBackgroundColor }) =>
    avatarBackgroundColor ?? theme.color.grey600};
  border-radius: 50%;
  display: flex;
  height: ${({ heightAndWidth }) => heightAndWidth}px;
  justify-content: center;
  width: ${({ heightAndWidth }) => heightAndWidth}px;

  ${({ showBorder, avatarBorderColor, theme }) =>
    showBorder && { border: `2px solid ${avatarBorderColor ?? theme.color.grey200}` }}

  svg path {
    fill: ${({ avatarIconColor }) => avatarIconColor ?? '#ffffff'};
  }
`;

const InitialsContainer = styled.div<{
  $backgroundColor: string;
  heightAndWidth: number;
  fontSize: number;
  fontWeight: number;
}>`
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 50%;
  color: white;
  display: flex;
  font-family: ${({ theme }) => theme.font.family.default};
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: ${({ fontWeight }) => fontWeight};
  height: ${({ heightAndWidth }) => heightAndWidth}px;
  justify-content: center;
  overflow: hidden;
  padding-top: 1px;
  width: ${({ heightAndWidth }) => heightAndWidth}px;

  a:hover & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 25%);
  }

  a:active & {
    box-shadow: inset 0 0 0 2px rgb(0 0 0 / 50%);
  }
`;

const Badge = styled.div<{
  badgeBackgroundColor?: Nilable<string>;
  badgeTextColor?: Nilable<string>;
}>`
  align-items: center;
  background-color: ${({ theme, badgeBackgroundColor }) =>
    badgeBackgroundColor ?? theme.color.brandBlue100};
  border-radius: 18px;
  bottom: 15px;
  color: ${({ theme, badgeTextColor }) => badgeTextColor ?? theme.color.brandBlue500};
  display: flex;
  font-size: 12px;
  font-weight: 500;
  height: 20px;
  justify-content: center;
  margin: 0 auto;
  padding: 0 6px 0 4px;
  position: relative;
  width: 60%;
`;

const AvatarWrapper = styled.div<{ maxHeight: number }>`
  max-height: ${({ maxHeight }) => maxHeight}px;
`;

type ImageLoadState = 'error' | 'loaded' | 'loading';
export type AvatarType = 'image' | 'initials';

type Props = {
  imageUrl?: string | null;
  name?: string | null;
  initials?: string | null;
  initialsFontSize?: number;
  initialsFontWeight?: number;
  shouldShowIcon?: boolean;
  showBorder?: boolean;
  onLoaded?: (avatarType: AvatarType) => void;
  heightAndWidth?: number;
  badgeText?: string;
  avatarBackgroundColor?: Nilable<string>;
  avatarIconColor?: Nilable<string>;
  avatarBorderColor?: Nilable<string>;
  badgeBackgroundColor?: Nilable<string>;
  badgeTextColor?: Nilable<string>;
};

export const SpeakerAvatar = ({
  imageUrl,
  name,
  initials,
  initialsFontSize = 12,
  initialsFontWeight = 700,
  shouldShowIcon = false,
  showBorder = true,
  onLoaded,
  heightAndWidth = 24,
  badgeText,
  avatarBackgroundColor,
  avatarIconColor,
  avatarBorderColor,
  badgeBackgroundColor,
  badgeTextColor,
}: Props): JSX.Element => {
  const [imageLoadState, setImageLoadState] = useState<ImageLoadState | null>(null);
  const themeColors = useThemeColor();
  const avatarColor = useMemo(() => {
    if (name == null || name === 'Anonymous') {
      return avatarBackgroundColor ?? themeColors.grey600;
    }

    return generateContactAvatarColors({
      seed: name,
      themeColors,
    })[0];
  }, [name, themeColors, avatarBackgroundColor]);

  const handleImageLoad = () => {
    setImageLoadState('loaded');
    onLoaded?.('image');
  };

  const handleImageLoadError = () => {
    setImageLoadState('error');
    onLoaded?.('initials');
  };

  const badge = isNonEmptyString(badgeText) ? (
    <Badge
      badgeBackgroundColor={badgeBackgroundColor}
      badgeTextColor={badgeTextColor}
    >
      {badgeText}
    </Badge>
  ) : null;

  if (isNil(imageUrl) || imageLoadState === 'error') {
    return (
      <AvatarWrapper maxHeight={heightAndWidth}>
        {shouldShowIcon ? (
          <AvatarIcon
            heightAndWidth={heightAndWidth}
            avatarBorderColor={avatarBorderColor}
            avatarBackgroundColor={avatarBackgroundColor}
            avatarIconColor={avatarIconColor}
            showBorder={showBorder}
          >
            <Icon type="users-permissions" />
          </AvatarIcon>
        ) : (
          <InitialsContainer
            $backgroundColor={avatarColor}
            heightAndWidth={heightAndWidth}
            fontSize={initialsFontSize}
            fontWeight={initialsFontWeight}
          >
            {initials ?? 'U'}
          </InitialsContainer>
        )}
        {badge}
      </AvatarWrapper>
    );
  }

  return (
    <AvatarWrapper maxHeight={heightAndWidth}>
      <AvatarImage
        key={imageUrl}
        src={imageUrl}
        alt={name ?? 'Unavailable'}
        onLoad={handleImageLoad}
        onError={handleImageLoadError}
        heightAndWidth={heightAndWidth}
        avatarBorderColor={avatarBorderColor}
        showBorder={showBorder}
      />
      {badge}
    </AvatarWrapper>
  );
};
